import {
  createClient,
  type AuthChangeEvent,
  type Session,
  type SupabaseClient,
} from "@supabase/supabase-js";
import type { BroadviewAuthClient, PasswordCredentials } from "@broadview/auth-client";

type SupabaseAuth = Pick<
  SupabaseClient["auth"],
  | "getSession"
  | "getUser"
  | "signInWithPassword"
  | "signInWithOtp"
  | "signOut"
  | "onAuthStateChange"
  | "resetPasswordForEmail"
  | "updateUser"
>;

export type PortalAuthClient = BroadviewAuthClient &
  Readonly<{
    isPasswordRecoveryCallback(): boolean;
    requestPasswordRecovery(email: string, redirectTo: string): Promise<void>;
    getProfile(): Promise<PortalAuthProfile>;
    updateProfile(profile: Readonly<{ displayName: string }>): Promise<void>;
    updatePassword(password: string): Promise<void>;
  }>;

export type PortalAuthProfile = Readonly<{
  email: string;
  displayName?: string;
}>;

type PortalAuthAdapterOptions = Readonly<{
  passwordRecoveryCallbackDetected?: boolean;
  requestPasswordRecovery?: (email: string, redirectTo: string) => Promise<void>;
}>;

export function isInvalidCredentialsError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  if ("code" in error && error.code === "invalid_credentials") return true;
  return (
    "message" in error &&
    typeof error.message === "string" &&
    error.message.toLowerCase() === "invalid login credentials"
  );
}

export function adaptPortalAuthClient(
  auth: SupabaseAuth,
  options: PortalAuthAdapterOptions = {},
): PortalAuthClient {
  return {
    async getAccessToken() {
      const { data, error } = await auth.getSession();
      if (error) throw error;
      return data.session?.access_token;
    },
    async hasSession() {
      const { data: sessionData, error: sessionError } = await auth.getSession();
      if (sessionError || !sessionData.session) return false;
      const { data, error } = await auth.getUser();
      return !error && Boolean(data.user);
    },
    async signInWithPassword(credentials: PasswordCredentials) {
      const { error } = await auth.signInWithPassword(credentials);
      if (error) throw error;
    },
    async sendMagicLink(email: string, redirectTo: string) {
      const { error } = await auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo, shouldCreateUser: false },
      });
      if (error) throw error;
    },
    isPasswordRecoveryCallback() {
      return options.passwordRecoveryCallbackDetected === true;
    },
    async requestPasswordRecovery(email: string, redirectTo: string) {
      if (options.requestPasswordRecovery) {
        await options.requestPasswordRecovery(email, redirectTo);
        return;
      }
      const { error } = await auth.resetPasswordForEmail(email, { redirectTo });
      if (error) throw error;
    },
    async getProfile() {
      const { data, error } = await auth.getUser();
      if (error) throw error;
      if (!data.user?.email) throw new Error("The authenticated profile has no email address.");
      const displayName = data.user.user_metadata.display_name;
      return {
        email: data.user.email,
        ...(typeof displayName === "string" && displayName.trim()
          ? { displayName: displayName.trim() }
          : {}),
      };
    },
    async updateProfile(profile) {
      const { error } = await auth.updateUser({
        data: { display_name: profile.displayName.trim() },
      });
      if (error) throw error;
    },
    async updatePassword(password: string) {
      const { error } = await auth.updateUser({ password });
      if (error) throw error;
    },
    async signOut() {
      const { error } = await auth.signOut();
      if (error) throw error;
    },
    onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void) {
      const { data } = auth.onAuthStateChange(callback);
      return () => data.subscription.unsubscribe();
    },
  };
}

export function createPortalAuthClient(url: string, publishableKey: string): PortalAuthClient {
  const passwordRecoveryCallbackDetected =
    new URLSearchParams(window.location.hash.slice(1)).get("type") === "recovery";
  const supabase = createClient(url, publishableKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
    },
  });

  // Recovery emails are frequently opened in a browser that does not have the
  // PKCE verifier that initiated the request. Use Supabase's browser-SPA
  // implicit flow only to create recovery links; the primary session and magic
  // link client remain on PKCE. Supabase returns recovery credentials in the URL
  // fragment, which is not sent to the web server.
  const recoverySupabase = createClient(url, publishableKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      flowType: "implicit",
      storageKey: "broadview-password-recovery-request",
    },
  });

  return adaptPortalAuthClient(supabase.auth, {
    passwordRecoveryCallbackDetected,
    async requestPasswordRecovery(email, redirectTo) {
      const { error } = await recoverySupabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });
      if (error) throw error;
    },
  });
}
